<template>
  <div class="chatbot-container" :style="containerStyle">
    <div
      v-if="!pageIsXS && showChat"
      class="resize-handle"
      :class="{ 'is-resizing': isResizing }"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize chat panel"
      @pointerdown="onResizePointerDown"
    ></div>
    <transition name="panel-slide">
      <deep-chat
        v-show="showChat"
        ref="deepChatRef"
        class="deep-chat"
        :connect="connection"
        :focusMode="{ smoothScroll: true, streamAutoScroll: false }"
        :customButtons
        :style
        :messageStyles
        :remarkable
        :inputAreaStyle
        :textInput
        :submitButtonStyles
        :auxiliaryStyle
        :loadHistory
        @message="onMessage"
      >
        <!--
          The intro panel may briefly be visible before the Deep Chat fully loads. To prevent this,
          set its display style to "none". Deep Chat will automatically set it to "block" once it is
          ready.
        -->
        <div style="display: none">
          <SuggestedPrompts
            :visible="showIntroContent"
            :message="introMessageOverride"
            @select="onSuggestedPromptClick"
          />
        </div>
      </deep-chat>
    </transition>
    <transition name="chat-tab-hide">
      <div
        v-show="!showChat"
        class="chat-tab"
        :class="{ 'has-unread': hasUnreadMessages }"
        @click="toggleChat"
      >
        <span class="chat-tab-text">Chat with Wiz</span>
      </div>
    </transition>
    <ChatbotPanelControls
      :visible="showChat"
      :theme="themeName"
      @select-theme="setTheme"
      @close="toggleChat"
    />
  </div>
</template>

<script>
const remarkable = {
  html: true,
  breaks: true,
  linkTarget: '_blank',
  typographer: true,
};

// Remarkable's built-in HTML tag regex only supports [A-Za-z0-9] in tag names,
// but web components require hyphens (e.g. <chatbot-response-toolbar>). This plugin adds
// an inline rule that recognises hyphenated element names and passes them through
// verbatim as htmltag tokens, so remarkable.html: true works for custom elements.
window.remarkable_plugins ??= [];
window.remarkable_plugins.push({
  plugin: function webComponentPlugin(md) {
    const WC_TAG_RE = /^<\/?([a-z][a-z0-9]*(?:-[a-z0-9]+)+)(\s[^>]*)?\/?>/i;
    md.inline.ruler.push('web_component', function (state, silent) {
      if (!state.options.html) {
        return false;
      }
      if (state.src.charCodeAt(state.pos) !== 0x3c) {
        return false;
      }
      const match = state.src.slice(state.pos).match(WC_TAG_RE);
      if (!match) {
        return false;
      }
      if (!silent) state.push({ type: 'htmltag', content: match[0], level: state.level });
      state.pos += match[0].length;
      return true;
    });
  },
});
</script>

<script setup>
import { ref, computed, watch, defineCustomElement, onMounted, onUnmounted } from 'vue';
import 'deep-chat';
import { useStore } from 'vuex';
import faEditIcon from '@fortawesome/fontawesome-pro/svgs/light/edit.svg?raw';
import faPaperPlaneIcon from '@fortawesome/fontawesome-pro/svgs/light/paper-plane.svg?raw';
import { everestApiUrl } from '@/app.config';
import {
  CHATBOT_PANEL_HORIZONTAL_PADDING,
  clampChatbotPanelWidth,
  fitChatbotPanelWidthToViewport,
  syncChatbotLayoutState,
} from '@/utils/chatbotLayout';
import { chatbotThemeVars } from '@/utils/chatbotThemes';
import { ElMessageBox } from 'element-plus';
import ChatbotResponseToolbar from './toolbar/ChatbotResponseToolbar.ce.vue';
import ChatbotToolApprovalRequest from './ChatbotToolApprovalRequest.ce.vue';
import ChatbotFunctionCall from './ChatbotFunctionCall.ce.vue';
import SuggestedPrompts, { suggestedPromptsStyle } from './SuggestedPrompts.vue';
import ChatbotPanelControls from './ChatbotPanelControls.vue';
import { deepChatRef } from './deepChat';

const ChatbotResponseToolbarElement = defineCustomElement(ChatbotResponseToolbar, {
  configureApp: app => {
    // Access ref from custom vue element
    app.provide('deepChatRef', deepChatRef);
  },
});
if (!customElements.get('chatbot-response-toolbar')) {
  customElements.define('chatbot-response-toolbar', ChatbotResponseToolbarElement);
}

const ChatbotToolApprovalRequestElement = defineCustomElement(ChatbotToolApprovalRequest, {
  configureApp: app => {
    // Access ref from custom vue element
    app.provide('deepChatRef', deepChatRef);
  },
});
if (!customElements.get('chatbot-tool-approval-request')) {
  customElements.define('chatbot-tool-approval-request', ChatbotToolApprovalRequestElement);
}

const ChatbotFunctionCallElement = defineCustomElement(ChatbotFunctionCall);
if (!customElements.get('chatbot-function-call')) {
  customElements.define('chatbot-function-call', ChatbotFunctionCallElement);
}

const store = useStore();
const clientId = computed(() => store.getters['client/clientId']);
const channelId = computed(() => store.state.leadType.leadType?.id);
const showChat = computed({
  get: () => store.getters['chatbot/isOpen'],
  set: val => store.commit('chatbot/setIsOpen', val),
});
const sessionId = computed(() => store.getters['chatbot/sessionId']);
const resetSessionId = () => store.commit('chatbot/resetSessionId');
const hasUnreadMessages = computed(() => store.getters['chatbot/hasUnreadMessages']);
const themeName = computed(() => store.getters['chatbot/theme']);
const setTheme = name => store.commit('chatbot/setTheme', name);

const pageIsXS = computed(() => store.getters['responsive/isXS']);
const storedPanelWidth = computed(() => store.getters['chatbot/panelWidth']);
// Total width, including padding. Drives the deep-chat width during and after a drag-resize.
const panelWidth = ref(storedPanelWidth.value);
const isResizing = ref(false);

watch(storedPanelWidth, newWidth => {
  if (!isResizing.value) {
    panelWidth.value = newWidth;
  }
});

const viewportWidth = computed(() => store.getters['responsive/width']);
// Shrink to fit a window that is narrower than the configured width, and snap back to the
// configured width once the window is wide enough to hold it again.
const effectivePanelWidth = computed(() =>
  fitChatbotPanelWidthToViewport(panelWidth.value, viewportWidth.value)
);

const style = computed(() => ({
  width: pageIsXS.value ? `100%` : `${effectivePanelWidth.value}px`,
  boxSizing: 'content-box',
  paddingRight: `${CHATBOT_PANEL_HORIZONTAL_PADDING}px`,
  height: '100%',
  /*
    Deep Chat fills in every property of its own default component style - which includes
    `background-color: white` and a `1px solid #cacaca` border on all four sides - that reads empty
    on its element when it renders, and it reads the element's inline style to decide. That makes
    the two rules below load-bearing:

    1. Background goes on the longhands rather than the `background` shorthand. A shorthand set to
       a var() leaves its longhands as pending-substitution values, which the CSSOM reports as the
       empty string; a longhand set to a var() reads back as that var() text, so it survives.
    2. The border values here contain no var(), so that `border-left` and friends can be
       serialized. A var() anywhere in a border longhand makes the whole side unserializable, which
       is why the themed color is applied by the .deep-chat rule in the style block below instead.

    Get either wrong and the panel renders white with a grey border until the next time Vue patches
    this style object - which is whenever something else here happens to re-render.
  */
  backgroundColor: 'var(--chatbot-panel-background-color)',
  backgroundImage: 'var(--chatbot-panel-background-image)',
  borderStyle: 'solid',
  borderWidth: '0 0 0 2px',
  borderColor: 'transparent',
  boxShadow: 'var(--chatbot-panel-shadow)',
}));

// Every color in the chat resolves against the custom properties declared here - see
// chatbotThemes.js for why the selected theme is applied as variables rather than by rebuilding
// the style objects above.
const containerStyle = computed(() => ({
  '--panel-total-width': `${effectivePanelWidth.value}px`,
  ...chatbotThemeVars(themeName.value),
}));

let dragStartX = 0;
let dragStartWidth = 0;
let pendingMoveEvent = null;
let pendingMoveFrame = null;

function applyPendingMove() {
  pendingMoveFrame = null;
  if (!pendingMoveEvent) {
    return;
  }
  panelWidth.value = clampChatbotPanelWidth(
    dragStartWidth + (dragStartX - pendingMoveEvent.clientX)
  );
  pendingMoveEvent = null;

  // Keep the shared --chatbot-panel-width var (consumed by page layouts/overlays to reserve
  // space alongside the panel) tracking the live drag width, not just the last-committed one.
  // App.vue only re-syncs this var from the Vuex getter, which doesn't update until
  // setPanelWidth runs on drag end - without this, page content resizes under/over the panel
  // during the drag and only snaps into place on release. Persisting to localStorage on every
  // frame would be wasteful, so that part still only happens once, in endResize.
  syncChatbotLayoutState({
    isOpen: true,
    isDesktop: true,
    width: effectivePanelWidth.value,
  });
}

function onResizePointerMove(event) {
  pendingMoveEvent = event;
  pendingMoveFrame ??= requestAnimationFrame(applyPendingMove);
}

function endResize() {
  isResizing.value = false;
  if (pendingMoveFrame) {
    cancelAnimationFrame(pendingMoveFrame);
    pendingMoveFrame = null;
  }
  applyPendingMove();
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
  document.removeEventListener('pointermove', onResizePointerMove);
  document.removeEventListener('pointerup', endResize);
  document.removeEventListener('pointercancel', endResize);
  store.commit('chatbot/setPanelWidth', panelWidth.value);
}

function onResizePointerDown(event) {
  isResizing.value = true;
  dragStartX = event.clientX;
  // Start from the width actually on screen, which is narrower than the configured width when
  // the window is too narrow to hold it. Otherwise the first part of the drag would be dead.
  dragStartWidth = effectivePanelWidth.value;
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
  document.addEventListener('pointermove', onResizePointerMove);
  document.addEventListener('pointerup', endResize);
  document.addEventListener('pointercancel', endResize);
}

const boxShadow = 'var(--chatbot-bubble-shadow)';
const messageStyles = {
  default: {
    ai: {
      bubble: {
        backgroundColor: 'var(--chatbot-ai-bubble-background)',
        color: 'var(--chatbot-text-color)',
        boxShadow,
        border: 'var(--chatbot-border-width) solid var(--chatbot-ai-bubble-border-color)',
        borderRadius: '10px',
        padding: '12px 16px',
        marginBottom: '24px',
        boxSizing: 'border-box',
      },
    },
    user: {
      bubble: {
        backgroundColor: 'var(--chatbot-user-bubble-background)',
        color: 'var(--chatbot-text-color)',
        border: 'var(--chatbot-border-width) solid var(--chatbot-user-bubble-border-color)',
        borderRadius: '10px',
        boxShadow,
        padding: '12px 16px',
        marginTop: '48px',
      },
    },
    shared: {
      bubble: {
        maxWidth: '100%',
      },
    },
  },
  loading: {
    message: {
      styles: {
        bubble: {
          backgroundColor: 'var(--chatbot-ai-bubble-background)',
          color: 'var(--chatbot-text-color)',
          boxShadow,
          border: 'var(--chatbot-border-width) solid var(--chatbot-ai-bubble-border-color)',
        },
      },
    },
    history: {
      full: {
        styles: {
          // Clear styles on innerContainer to prevent a bubble from rendering behind the loading spinner.
          innerContainer: {},
          bubble: {
            color: 'var(--chatbot-loading-color)',
          },
        },
      },
    },
  },
  error: {
    bubble: {
      backgroundColor: 'var(--chatbot-error-bubble-background)',
      color: 'var(--chatbot-error-bubble-color)',
      border: 'var(--chatbot-border-width) solid var(--chatbot-error-bubble-border-color)',
    },
  },
};

const inputAreaStyle = {
  background: 'none',
};

const textInput = {
  styles: {
    text: {
      color: 'var(--chatbot-text-color)',
      padding: '12px 16px',
    },
    container: {
      margin: '0 0 24px 0',
      background: 'var(--chatbot-surface-background)',
      border: 'var(--chatbot-border-width) solid var(--chatbot-surface-border-color)',
      borderRadius: '10px',
      boxShadow,
    },
    focus: {
      background: 'var(--chatbot-highlight-background)',
      border: 'var(--chatbot-border-width) solid var(--chatbot-highlight-border-color)',
    },
  },
  placeholder: {
    text: 'Ask me anything!',
    style: {
      color: 'var(--chatbot-placeholder-color)',
    },
  },
};

const submitButtonStyles = {
  submit: {
    container: {
      default: {
        margin: '0 4px 20px 0',
      },
    },
    svg: {
      styles: {
        default: {
          width: '20px',
          height: '20px',
          filter: 'none',
          fill: 'var(--chatbot-icon-color)',
        },
      },
      content: faPaperPlaneIcon,
    },
  },
  loading: {
    svg: {
      styles: {
        default: {
          filter: 'none',
          fill: 'var(--chatbot-icon-color-muted)',
        },
      },
    },
  },
};

const auxiliaryStyle = `
  ::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
  ::-webkit-scrollbar-thumb {
    background-color: var(--chatbot-scrollbar-thumb-background);
    border-radius: 3px;
  }
  ::-webkit-scrollbar-track {
    background-color: transparent;
  }
  /*
    Sometimes the first AI message is wrapped in a div and sometimes it's not.
    Add top margin to avoid overlapping the panel controls.
  */
  #messages > :first-child.deep-chat-outer-container-role-ai,
  #messages > :first-child:has(> .deep-chat-outer-container-role-ai),
  #messages > :nth-child(1 of :has(> .deep-chat-outer-container-role-ai)) {
    margin-top: 36px;
  }
  .ai-message p {
    margin: 0;
  }
  chatbot-response-toolbar {
    margin-top: 6px;
  }
  a {
    color: var(--chatbot-link-color);
    text-decoration: none;
  }
  ${suggestedPromptsStyle}
`;

const connection = ref({
  url: `${everestApiUrl}/api/prompt`,
  method: 'POST',
  headers: {
    'Session-Id': sessionId,
    'Client-Id': clientId,
    'Channel-Id': channelId,
  },
  stream: true,
});

// Due to potential overlap with the loading indicator, showIntroContent withholds the content until
// loadHistory has finished and found no history, or the chat session is cleared.
const showIntroContent = ref(false);
// Replaces the intro panel's default description - e.g. with newSessionMessage below - while
// still showing the current page's suggestions under it. Reset to null wherever the panel is
// shown for a reason other than a session reset, so it doesn't show a stale message.
const introMessageOverride = ref(null);

function onSuggestedPromptClick(suggestion) {
  deepChatRef.value.submitUserMessage({
    text: suggestion.text,
    custom: { promptName: suggestion.name, promptVersion: suggestion.version },
  });
}

const newSessionMessage =
  "You've switched clients, so I started a new chat to stay focused on this one.";

// Handles client changes after the initial history has loaded.
watch(clientId, async newClientId => {
  const sessionReset = await store.dispatch('chatbot/bindSessionToClient', newClientId);
  if (sessionReset) {
    clearAllMessages();
    introMessageOverride.value = newSessionMessage;
    showIntroContent.value = true;
  }
});

const loadHistory = async () => {
  // Keep the intro panel's content hidden behind the loading indicator until this load
  // resolves, including when Deep Chat re-renders and loads the history again.
  showIntroContent.value = false;
  introMessageOverride.value = null;

  // Resets the session if the page is on a different client than the persisted session.
  const sessionReset = await store.dispatch('chatbot/bindSessionToClient', clientId.value);
  if (sessionReset) {
    introMessageOverride.value = newSessionMessage;
    showIntroContent.value = true;
    return [];
  }

  const initialSessionId = sessionId.value;
  const history = await store.dispatch('chatbot/loadHistory');

  // Discard stale history if the session was reset while loading. The client watcher that
  // reset the session shows its own intro panel message, which this would otherwise replace.
  if (sessionId.value !== initialSessionId) {
    return [];
  }

  // An empty session returns no messages, so Deep Chat leaves the intro panel displayed and
  // its content - the welcome greeting and this page's suggested prompts - is revealed.
  showIntroContent.value = history.length === 0;

  return history;
};

const originalTitle = document.title;
let titleInterval = null;

function startTitleAlternation() {
  if (titleInterval) {
    return;
  }
  titleInterval = setInterval(() => {
    document.title = document.title === originalTitle ? '💬 New Message' : originalTitle;
  }, 1500);
}

function stopTitleAlternation() {
  if (titleInterval) {
    clearInterval(titleInterval);
    titleInterval = null;
  }
  document.title = originalTitle;
}

function handleVisibilityChange() {
  if (!document.hidden && showChat.value) {
    stopTitleAlternation();
  }
}

function handleKeydown(event) {
  if (event.altKey && event.code === 'KeyN' && showChat.value) {
    event.preventDefault();
    confirmNewChat();
  }
}

document.addEventListener('visibilitychange', handleVisibilityChange);
document.addEventListener('keydown', handleKeydown);

function onMessage(event) {
  const { message, isHistory } = event.detail;
  if (isHistory) {
    return;
  }
  if (message.role === 'user') {
    store.commit('chatbot/setSessionHasUserMessages', true);
    return;
  }
  if (message.role === 'ai') {
    if (!showChat.value) {
      store.commit('chatbot/setHasUnreadMessages', true);
    }
    if (document.hidden) {
      startTitleAlternation();
    }
  }
}

const isMac = /Mac/i.test(navigator.userAgent);
const newChatShortcut = isMac ? '⌥N' : 'Alt+N';

const customButtons = [
  {
    position: 'outside-start',
    tooltip: {
      text: `Start New Chat (${newChatShortcut})`,
    },
    styles: {
      button: {
        default: {
          container: {
            default: {
              margin: '0 4px 20px 0',
            },
          },
          svg: {
            content: faEditIcon,
            styles: {
              default: {
                width: '20px',
                height: '20px',
                fill: 'var(--chatbot-icon-color)',
                filter: 'none',
              },
            },
          },
        },
      },
    },
    onClick: confirmNewChat,
  },
];

function clearAllMessages(showIntroPanel = true) {
  // Currently deep-chat retains streamed messages. This hack removes the associated class so they can be cleared.
  // https://github.com/OvidijusParsiunas/deep-chat/issues/492
  deepChatRef.value.shadowRoot
    .querySelectorAll('.streamed-message')
    .forEach(el => el.classList.remove('streamed-message'));

  deepChatRef.value.clearMessages(showIntroPanel);
}

function confirmNewChat() {
  ElMessageBox.confirm('Your previous messages will be cleared.', 'Start New Chat', {
    confirmButtonText: 'OK',
    cancelButtonText: 'Cancel',
    type: 'warning',
    callback: action => {
      if (action === 'confirm') {
        clearAllMessages();
        resetSessionId();
        // The chat is empty again, so Deep Chat re-displays the panel with its content.
        introMessageOverride.value = null;
        showIntroContent.value = true;
        deepChatRef.value.focusInput();
      }
    },
  });
}

function toggleChat() {
  showChat.value = !showChat.value;
  if (showChat.value) {
    store.commit('chatbot/setHasUnreadMessages', false);
    stopTitleAlternation();
  }
}

onMounted(() => {
  deepChatRef.value.requestInterceptor = async requestDetails => {
    const token = await store.dispatch('auth/getToken', null, { root: true });
    requestDetails.headers.Authorization = `Bearer ${token}`;
    return requestDetails;
  };
});

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  document.removeEventListener('keydown', handleKeydown);
  stopTitleAlternation();
  if (isResizing.value) {
    endResize();
  }
});
</script>

<style scoped>
.chatbot-container {
  position: relative;
  display: flex;
  flex-direction: row;
  height: 100%;
}

.deep-chat {
  /* Display over chat-tab (z-index: 3000) */
  z-index: 3500;

  /*
    Only the panel's border color, because a var() in an inline border declaration is what makes
    Deep Chat overwrite the border - see the `style` computed above. The !important is what beats
    that inline declaration from a stylesheet.
  */
  border-left-color: var(--chatbot-panel-border-color) !important;
}

.resize-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -3px;
  width: 8px;
  cursor: col-resize;
  touch-action: none;
  /* Display over .deep-chat (z-index: 3500) */
  z-index: 4000;
}

.resize-handle:hover,
.resize-handle.is-resizing {
  background: var(--chatbot-resize-handle-background);
}

/*
  Deliberately not themed: the tab is the chat's entry point on the page rather than part of the
  panel, so it keeps one look no matter which theme is selected inside.
*/
.chat-tab {
  position: fixed;
  right: 0;
  bottom: 24px;
  background: linear-gradient(to right, teal, #0a1d28);
  color: white;
  padding: 16px 8px;
  cursor: pointer;
  /* Display over loading masks (z-index: 2000) */
  z-index: 3000;
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  box-shadow: -2px 0px 8px rgba(0, 117, 117, 0.4);
  border-left: 2px solid #007575;
  transition: all 200ms ease-out;

  &:hover {
    background: linear-gradient(to right, #009999, #0e2a3a);
    padding-right: 16px;
  }

  &:active {
    background: linear-gradient(to right, teal, #0a1d28);
  }
}

.chat-tab-text {
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 2px;
}

.chat-tab.has-unread {
  animation: tab-flash-bounce 2s ease-in-out infinite;
}

@keyframes tab-flash-bounce {
  0%,
  100% {
    padding-right: 8px;
    filter: brightness(1);
  }
  20%,
  50% {
    padding-right: 20px;
    filter: brightness(1.4);
  }
  35% {
    padding-right: 10px;
    filter: brightness(1.1);
  }
  65% {
    padding-right: 8px;
    filter: brightness(1);
  }
}

.panel-slide-enter-active,
.panel-slide-leave-active {
  transition: transform 400ms cubic-bezier(0.39, 0.71, 0.54, 1.03);
}

.panel-slide-enter-from,
.panel-slide-leave-to {
  transform: translateX(100%);
}

/* Delay hiding of the chat tab so the panel appears to slide over it. */
.chat-tab-hide-enter-active,
.chat-tab-hide-leave-active {
  transition-property: display;
  transition-duration: 400ms;
}
</style>
